import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { User, Building2 } from 'lucide-react';
import { useCRMAuth } from '../../contexts/CRMAuthContext';
import { useToast } from '../../hooks/use-toast';
import { useLanguage } from '../../contexts/LanguageContext';

const ProfileCompletion = ({ onComplete }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { updateProfile } = useCRMAuth();
  const { toast } = useToast();
  const { t } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName.trim()) {
      toast({
        title: t('profile.t.nameRequiredTitle'),
        description: t('profile.t.nameRequiredDesc'),
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const fullName = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();
      
      const result = await updateProfile({
        name: fullName,
        company: formData.company.trim() || undefined
      });

      if (result.success) {
        toast({
          title: t('profile.t.completedTitle'),
          description: t('profile.t.completedDesc', { name: fullName }),
        });
        
        if (onComplete) {
          onComplete(result.user);
        }
      } else {
        throw new Error(result.error || 'Failed to update profile');
      }
    } catch (error) {
      toast({
        title: t('profile.t.updateFailedTitle'),
        description: error.message || t('profile.t.updateFailedDesc'),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mb-4">
            <User className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-2xl">{t('profile.title')}</CardTitle>
          <CardDescription>
            {t('profile.desc')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="firstName">{t('profile.firstName')}</Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                placeholder={t('profile.firstNamePh')}
                autoFocus
                disabled={isSubmitting}
              />
            </div>

            <div>
              <Label htmlFor="lastName">{t('profile.lastName')}</Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                placeholder={t('profile.lastNamePh')}
                disabled={isSubmitting}
              />
            </div>

            <div>
              <Label htmlFor="company">{t('profile.company')}</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  placeholder={t('profile.companyPh')}
                  className="pl-10"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting || !formData.firstName.trim()}
            >
              {isSubmitting ? t('profile.saving') : t('profile.complete')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileCompletion;
